<script setup lang="ts">
/**
 * LoginGateModal — 랜딩(WS-00)에서 비로그인 사용자가 회원 전용 축에 진입을 시도할 때 띄우는
 * 로그인 유도 모달. 문안은 퍼블리싱 `LoginGate`(landing-shell.jsx) 이식 — 대상 축명 볼드 바인딩.
 *
 *  - [다음에 하기]: 모달만 닫고 랜딩 유지(update:open false).
 *  - [로그인 · 가입하기]: returnUrl 을 보존(구글 SSO 전체 리다이렉트 생존용 sessionStorage +
 *    로컬 로그인용 redirect 쿼리 — AuthRequiredPage 와 동일 이중 보존)하고 공통 로그인으로 이동.
 *
 * 접근성·오버레이는 기존 TestIntroModal/LegalModal 과 동일하게 reka-ui Dialog 가 처리
 * (focus trap · ESC · 바깥 클릭 닫기 · 배경 스크롤 잠금). 신규 인증 로직 0(research R4).
 */
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
} from 'reka-ui';
import { X } from 'lucide-vue-next';
import { useRouter } from 'vue-router';

import BaseButton from '#/components/ui/BaseButton.vue';
import { useAuthStore } from '#/stores/auth';

interface Props {
  /** 모달 열림 상태(부모 v-model:open). */
  open: boolean;
  /** 진입하려던 축명(볼드 표기) — [시작하기] 경유 등 축 미특정이면 null. */
  targetName?: string | null;
  /** 로그인 성공 후 복귀할 대상 경로(EX-1 returnUrl). */
  redirect: string;
}

const props = withDefaults(defineProps<Props>(), { targetName: null });

const emit = defineEmits<{
  /** 열림 상태 변경(v-model:open). */
  'update:open': [value: boolean];
}>();

const router = useRouter();
const auth = useAuthStore();

/** [로그인 · 가입하기] — 복귀지 이중 보존 후 공통 로그인 화면으로 이동. */
function goLogin() {
  auth.setReturnUrl(props.redirect);
  emit('update:open', false);
  void router.push({ path: '/login', query: { redirect: props.redirect } });
}
</script>

<template>
  <DialogRoot :open="open" @update:open="$emit('update:open', $event)">
    <DialogPortal>
      <DialogOverlay
        class="fixed inset-0 z-modal bg-overlay backdrop-blur-[2px] data-[state=open]:animate-fo-fade"
      />
      <DialogContent
        class="fixed bottom-0 left-1/2 z-modal flex max-h-[85vh] w-full -translate-x-1/2 flex-col overflow-hidden rounded-t-[20px] border border-line bg-white shadow-lg focus:outline-none md:bottom-auto md:top-1/2 md:max-h-[80vh] md:max-w-[440px] md:-translate-y-1/2 md:rounded-modal"
      >
        <!-- 헤더(제목 + 닫기) -->
        <div class="flex items-center justify-between gap-3 border-b border-line px-6 py-5">
          <DialogTitle class="m-0 text-[18px] font-extrabold tracking-[-0.02em] text-brand">
            로그인하면 바로 이어집니다
          </DialogTitle>
          <DialogClose
            class="flex h-9 w-9 flex-none items-center justify-center rounded-full border border-line bg-white text-brand transition-colors hover:border-accent hover:text-accent-deep"
            aria-label="닫기"
          >
            <X class="h-[18px] w-[18px]" :stroke-width="2" />
          </DialogClose>
        </div>

        <!-- 본문 — 퍼블리싱 LoginGate 문안 -->
        <div class="px-6 py-5">
          <DialogDescription class="m-0 text-[14px] leading-[1.8] text-body">
            <template v-if="targetName"
              ><b class="font-extrabold text-brand">{{ targetName }}</b> 기능은 </template
            ><template v-else>이 기능은 </template>저장된 성적·검사 결과를 사용합니다. 가입은
            무료이며, 한 번 입력한 정보는 모든 분석에서 계속 쓰입니다.
          </DialogDescription>
        </div>

        <!-- 푸터 액션 -->
        <div class="flex justify-end gap-2.5 border-t border-line px-6 py-4">
          <BaseButton variant="line" @click="$emit('update:open', false)">다음에 하기</BaseButton>
          <BaseButton @click="goLogin">로그인 · 가입하기</BaseButton>
        </div>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>
